import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { contentAPI, profilesAPI } from '../services/api';
import { useToast } from './Toast';
import {
  DOCUMENT_FORMATS,
  defaultWordCountForFormat,
  formatLabel,
  normalizeDocumentFormat,
} from '../constants/documentFormat';
import '../styles/WorkspaceChrome.css';
import './ContentQueue.css';

function DocumentFormatSelect({ id, value, onChange, hint }) {
  return (
    <div className="form-group">
      <label htmlFor={id}>Document format</label>
      {hint && <p className="pf-queue-field-hint">{hint}</p>}
      <select id={id} value={value} onChange={onChange} className="pf-filter-select pf-select-block">
        {DOCUMENT_FORMATS.map((f) => (
          <option key={f.value} value={f.value}>
            {f.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Editable list of short text rows (add / edit / remove). */
function EditableStringList({ items, onChange, placeholder, addLabel }) {
  const update = (idx, val) => {
    const next = items.slice();
    next[idx] = val;
    onChange(next);
  };
  const remove = (idx) => {
    const next = items.slice();
    next.splice(idx, 1);
    onChange(next);
  };
  return (
    <div className="pf-angle-list">
      {items.map((item, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="pf-angle-list-row" key={idx}>
          <input
            type="text"
            className="pf-angle-list-input"
            value={item}
            placeholder={placeholder}
            onChange={(e) => update(idx, e.target.value)}
          />
          <button
            type="button"
            className="pf-angle-list-remove"
            onClick={() => remove(idx)}
            aria-label="Remove item"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className="pf-btn pf-btn-inline pf-angle-list-add"
        onClick={() => onChange([...items, ''])}
      >
        {addLabel}
      </button>
    </div>
  );
}

function ContentQueue() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [queueItems, setQueueItems] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [statusFilter, setStatusFilter] = useState('queued');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatingBrief, setCreatingBrief] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [showBriefModal, setShowBriefModal] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [briefNotes, setBriefNotes] = useState('');
  const [briefSeedUrls, setBriefSeedUrls] = useState('');
  const [briefTargetWordCount, setBriefTargetWordCount] = useState('');
  const [briefDocumentFormat, setBriefDocumentFormat] = useState('blog');
  const [briefStep, setBriefStep] = useState('setup');
  const [proposingAngle, setProposingAngle] = useState(false);
  const [anglePrimary, setAnglePrimary] = useState('');
  const [angleSupporting, setAngleSupporting] = useState([]);
  const [angleOutOfScope, setAngleOutOfScope] = useState([]);
  const [editFormData, setEditFormData] = useState({
    title: '',
    target_keyword: '',
    priority: 5,
    justification: '',
    document_format: 'blog',
  });
  const [addFormData, setAddFormData] = useState({
    profile_id: '',
    title: '',
    target_keyword: '',
    priority: 5,
    justification: '',
    document_format: 'blog',
  });

  useEffect(() => {
    loadData();
  }, [selectedProfile, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (selectedProfile) params.profile_id = selectedProfile;
      if (statusFilter) params.status = statusFilter;

      const [queueRes, profilesRes] = await Promise.all([
        contentAPI.getQueue(params),
        profilesAPI.getProfiles(),
      ]);

      setQueueItems(queueRes.data);
      setProfiles(profilesRes.data);
    } catch (err) {
      console.error('Error loading queue:', err);
      setError(`Failed to load content queue: ${err.response?.data?.error || err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const resetAngleFields = useCallback(() => {
    setAnglePrimary('');
    setAngleSupporting([]);
    setAngleOutOfScope([]);
  }, []);

  const handleCreateBriefClick = (queueId) => {
    const item = queueItems.find((i) => i.id === queueId);
    const fmt = normalizeDocumentFormat(item?.research_context?.document_format);
    setShowBriefModal(queueId);
    setBriefStep('setup');
    setBriefNotes('');
    setBriefSeedUrls('');
    setBriefDocumentFormat(fmt);
    setBriefTargetWordCount(String(defaultWordCountForFormat(fmt)));
    resetAngleFields();
  };

  const handleBriefFormatChange = (e) => {
    const fmt = e.target.value;
    setBriefDocumentFormat(fmt);
    setBriefTargetWordCount(String(defaultWordCountForFormat(fmt)));
  };

  const handleProposeAngle = async (queueId) => {
    try {
      setProposingAngle(true);
      setError(null);

      const body = {};
      if (briefNotes.trim()) body.additional_notes = briefNotes.trim();
      const wc = parseInt(briefTargetWordCount, 10);
      if (Number.isFinite(wc) && wc > 0) body.target_word_count = wc;
      body.document_format = briefDocumentFormat;

      const response = await contentAPI.proposeArticleAngle(queueId, body);
      setAnglePrimary(response.data.primary_angle || '');
      setAngleSupporting(response.data.supporting_points || []);
      setAngleOutOfScope(response.data.out_of_scope || []);
      setBriefStep('angle');
    } catch (err) {
      console.error('Error proposing article angle:', err);
      showToast(
        err.response?.data?.error || err.message || 'Failed to propose article angle',
        'error'
      );
    } finally {
      setProposingAngle(false);
    }
  };

  const handleCreateResearchBrief = async (queueId) => {
    try {
      setCreatingBrief((prev) => ({ ...prev, [queueId]: true }));
      setError(null);
      setShowBriefModal(null);

      const body = {};
      if (briefNotes.trim()) body.additional_notes = briefNotes.trim();
      const seedLines = briefSeedUrls
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      if (seedLines.length > 0) body.seed_sources = seedLines;
      const wc = parseInt(briefTargetWordCount, 10);
      if (Number.isFinite(wc) && wc > 0) body.target_word_count = wc;
      body.document_format = briefDocumentFormat;

      const primary = anglePrimary.trim();
      const supporting = angleSupporting.map((s) => s.trim()).filter(Boolean);
      const outOfScope = angleOutOfScope.map((s) => s.trim()).filter(Boolean);
      if (primary || supporting.length > 0 || outOfScope.length > 0) {
        body.article_angle = {
          primary_angle: primary,
          supporting_points: supporting,
          out_of_scope: outOfScope,
        };
      }

      const response = await contentAPI.createResearchBrief(queueId, body);
      const draftId = response.data.draft_id;

      if (response.data.status === 'exists') {
        showToast('A draft already exists for this item — opening workspace.', 'info', 4000);
      } else {
        showToast('Research brief created. Opening workspace…', 'success', 4000);
      }

      setBriefNotes('');
      setBriefSeedUrls('');
      setBriefTargetWordCount('');
      setBriefDocumentFormat('blog');
      setBriefStep('setup');
      resetAngleFields();
      loadData();
      navigate(`/research-brief/${draftId}`);
    } catch (err) {
      console.error('Error creating research brief:', err);
      setError(
        `Failed to create research brief: ${err.response?.data?.error || err.message || 'Unknown error'}`
      );
      showToast(
        err.response?.data?.error || err.message || 'Failed to create research brief',
        'error'
      );
    } finally {
      setCreatingBrief((prev) => ({ ...prev, [queueId]: false }));
    }
  };

  const handleCancelBriefModal = useCallback(() => {
    setShowBriefModal(null);
    setBriefStep('setup');
    setBriefNotes('');
    setBriefSeedUrls('');
    setBriefTargetWordCount('');
    setBriefDocumentFormat('blog');
    resetAngleFields();
  }, [resetAngleFields]);

  const handleEdit = (item) => {
    setEditingItem(item);
    setEditFormData({
      title: item.title || '',
      target_keyword: item.target_keyword || '',
      priority: item.priority || 5,
      justification: item.research_context?.justification || '',
      document_format: normalizeDocumentFormat(item.research_context?.document_format),
    });
  };

  const handleCancelEdit = useCallback(() => {
    setEditingItem(null);
    setEditFormData({
      title: '',
      target_keyword: '',
      priority: 5,
      justification: '',
      document_format: 'blog',
    });
  }, []);

  const handleSaveEdit = async () => {
    if (!editingItem) return;

    try {
      setError(null);
      const updateData = {
        title: editFormData.title,
        target_keyword: editFormData.target_keyword || null,
        priority: editFormData.priority,
        document_format: editFormData.document_format,
      };

      if (editFormData.justification !== (editingItem.research_context?.justification || '')) {
        updateData.justification = editFormData.justification;
      }

      await contentAPI.updateQueueItem(editingItem.id, updateData);
      showToast('Queue item updated successfully!', 'success');
      setEditingItem(null);
      loadData();
    } catch (err) {
      console.error('Error updating queue item:', err);
      showToast(`Failed to update queue item: ${err.response?.data?.error || err.message || 'Unknown error'}`, 'error');
    }
  };

  const handleDelete = async (itemId) => {
    const item = queueItems.find(i => i.id === itemId);
    if (!confirm(`Are you sure you want to delete "${item?.title}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setError(null);
      await contentAPI.deleteQueueItem(itemId);
      showToast('Queue item deleted successfully!', 'success');
      loadData();
    } catch (err) {
      console.error('Error deleting queue item:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
      showToast(`Failed to delete queue item: ${errorMsg}`, 'error');
      setError(errorMsg);
    }
  };

  const handleAddArticle = () => {
    setShowAddModal(true);
    setAddFormData({
      profile_id: selectedProfile || '',
      title: '',
      target_keyword: '',
      priority: 5,
      justification: '',
      document_format: 'blog',
    });
  };

  const handleCancelAdd = useCallback(() => {
    setShowAddModal(false);
    setAddFormData({
      profile_id: '',
      title: '',
      target_keyword: '',
      priority: 5,
      justification: '',
      document_format: 'blog',
    });
  }, []);

  const handleSubmitAdd = async () => {
    if (!addFormData.title || !addFormData.title.trim()) {
      showToast('Please enter a title', 'warning');
      return;
    }

    try {
      setError(null);
      await contentAPI.createQueueItem({
        ...addFormData,
        profile_id: addFormData.profile_id || null,
        title: addFormData.title.trim(),
      });
      showToast('Article added to queue successfully!', 'success');
      setShowAddModal(false);
      loadData();
    } catch (err) {
      console.error('Error creating queue item:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
      showToast(`Failed to add article: ${errorMsg}`, 'error');
      setError(errorMsg);
    }
  };

  useEffect(() => {
    const modalOpen =
      showBriefModal != null || showAddModal || editingItem != null;
    if (!modalOpen) return undefined;
    // Intentionally do not close on Escape — form dialogs require Cancel / close button
    // so accidental keypresses don't discard in-progress input.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showBriefModal, showAddModal, editingItem]);

  if (loading) {
    return (
      <div className="content-queue pf-explore">
        <div className="pf-loading">Loading draft candidates…</div>
      </div>
    );
  }

  return (
    <div className="content-queue pf-explore">
      <div className="pf-header">
        <div>
          <h2>Draft candidates</h2>
          <p className="pf-sub">
            Approved research angles and manual additions. Create a draft workspace to research, outline, and write.
          </p>
        </div>
        <div className="pf-filters">
          <select
            value={selectedProfile}
            onChange={(e) => setSelectedProfile(e.target.value)}
            className="pf-filter-select"
            aria-label="Filter by topic profile"
          >
            <option value="">All topic profiles</option>
            {profiles.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pf-filter-select"
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            <option value="queued">Queued</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
          </select>
          <button type="button" onClick={handleAddArticle} className="pf-btn pf-btn-primary">
            Add draft candidate
          </button>
        </div>
      </div>

      {error && <div className="pf-error-banner">{error}</div>}

      <div className="queue-list pf-list-card">
        <h3>Draft candidates ({queueItems.length})</h3>
        {queueItems.length === 0 ? (
          <div className="pf-empty">
            <p>No articles in the queue.</p>
            <p className="pf-empty-hint">
              Approve ideas from Research reports or manually add articles to the queue.
            </p>
          </div>
        ) : (
          queueItems.map((item) => {
            const docFmt = normalizeDocumentFormat(item.research_context?.document_format);
            return (
            <div key={item.id} className="queue-item">
              <div className="queue-item-header">
                <div className="queue-item-title-section">
                  <h3 className="queue-item-title">{item.title}</h3>
                  <div className="queue-item-meta">
                    {item.profile_name ? (
                      <span className="meta-badge profile-badge">{item.profile_name}</span>
                    ) : (
                      <span className="meta-badge one-off-queue-badge">One-off</span>
                    )}
                    {item.target_keyword && (
                      <span className="meta-badge keyword-badge">Keyword: {item.target_keyword}</span>
                    )}
                    {docFmt !== 'blog' && (
                      <span className="meta-badge format-badge">{formatLabel(docFmt)}</span>
                    )}
                    <span className={`meta-badge priority-badge priority-${item.priority}`}>
                      Priority: {item.priority}/10
                    </span>
                    <span className={`meta-badge status-badge status-${item.status}`}>
                      {item.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="queue-item-actions">
                {item.status === 'queued' && !item.has_draft && (
                  <div className="generate-actions">
                    <button
                      type="button"
                      onClick={() => handleCreateBriefClick(item.id)}
                      disabled={creatingBrief[item.id]}
                      className="pf-btn pf-btn-primary create-brief-btn"
                    >
                      {creatingBrief[item.id] ? 'Starting…' : 'Create Draft Workspace'}
                    </button>
                  </div>
                )}
                {item.has_draft && (
                  <span className="draft-status">
                    Draft exists — open <strong>Draft workspaces</strong> or use your last workspace link.
                  </span>
                )}
                {item.status === 'in_progress' && (
                  <span className="draft-status">In progress</span>
                )}
              </div>

              <div className="queue-item-footer">
                <span className="queue-date">
                  Added: {new Date(item.created_at).toLocaleDateString()}
                </span>
                {item.scheduled_for && (
                  <span className="queue-date">
                    Scheduled: {new Date(item.scheduled_for).toLocaleDateString()}
                  </span>
                )}
                <div className="queue-item-footer-actions">
                  {item.status === 'queued' && !item.has_draft && (
                    <button
                      type="button"
                      onClick={() => handleEdit(item)}
                      className="pf-btn pf-btn-inline"
                      title="Edit queue item"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    className="pf-btn pf-btn-inline-danger"
                    title="Delete queue item"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
            );
          })
        )}
      </div>

      {editingItem && (
        <EditQueueItemModal
          item={editingItem}
          formData={editFormData}
          setFormData={setEditFormData}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
        />
      )}

      {showBriefModal && (
        <div className="pf-modal-backdrop" role="presentation">
          <div
            className="pf-modal-panel pf-modal-panel--queue-brief"
            role="dialog"
            aria-modal="true"
            aria-labelledby="brief-modal-title"
          >
            <div className="pf-modal-head">
              <h3 id="brief-modal-title">Create Draft Workspace</h3>
              <button
                type="button"
                className="pf-modal-close"
                aria-label="Close"
                onClick={handleCancelBriefModal}
              >
                ×
              </button>
            </div>
            <p className="pf-wizard-step" aria-hidden="true">
              {briefStep === 'setup' ? 'Step 1 of 2 · Setup' : 'Step 2 of 2 · Confirm direction'}
            </p>

            {briefStep === 'setup' && (
              <>
                <p className="pf-queue-modal-intro">
                  Set up the research pass. Next, we&apos;ll propose an article direction for you to
                  confirm — we only research and outline after you approve it.
                </p>
                <DocumentFormatSelect
                  id="brief-document-format"
                  value={briefDocumentFormat}
                  onChange={handleBriefFormatChange}
                  hint="Case studies, white papers, and integration guides use tailored outline structures."
                />
                <label className="pf-queue-field-label">Research direction (optional)</label>
                <p className="pf-queue-field-hint">Audience, angle, tone, goals — guides the proposed direction and research pass.</p>
                <textarea
                  value={briefNotes}
                  onChange={(e) => setBriefNotes(e.target.value)}
                  placeholder="e.g., Emphasize SMB buyers; compare two vendor approaches"
                  rows={3}
                  className="pf-queue-textarea"
                />
                <label className="pf-queue-field-label" htmlFor="brief-target-word-count">
                  Target word count (optional)
                </label>
                <p className="pf-queue-field-hint">
                  Guides outline depth — shorter targets produce fewer sections and bullets.
                </p>
                <div className="pf-queue-wordcount-row">
                  <input
                    id="brief-target-word-count"
                    type="number"
                    min={300}
                    max={20000}
                    step={100}
                    value={briefTargetWordCount}
                    onChange={(e) => setBriefTargetWordCount(e.target.value)}
                    placeholder="1200"
                    className="pf-queue-wordcount-input"
                  />
                  <div className="pf-queue-wordcount-presets" aria-label="Word count presets">
                    {[800, 1200, 1500, 2500, 4000].map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`pf-queue-wordcount-preset${briefTargetWordCount === String(n) ? ' active' : ''}`}
                        onClick={() => setBriefTargetWordCount(String(n))}
                      >
                        {n.toLocaleString()}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="pf-queue-field-label">Seed sources (optional)</label>
                <p className="pf-queue-field-hint">
                  URLs to prioritize in the research pass (whitepapers, internal reports, authorities,
                  studies). One URL per line.
                </p>
                <textarea
                  value={briefSeedUrls}
                  onChange={(e) => setBriefSeedUrls(e.target.value)}
                  placeholder={'https://example.com/report\nhttps://…'}
                  rows={4}
                  className="pf-queue-textarea"
                  spellCheck={false}
                />
                <div className="pf-modal-actions">
                  <button
                    type="button"
                    onClick={handleCancelBriefModal}
                    className="pf-btn pf-btn-secondary"
                    disabled={proposingAngle}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleProposeAngle(showBriefModal)}
                    disabled={proposingAngle}
                    className="pf-btn pf-btn-primary"
                  >
                    {proposingAngle ? 'Proposing…' : 'Continue — propose angle'}
                  </button>
                </div>
              </>
            )}

            {briefStep === 'angle' && (
              <>
                <p className="pf-queue-modal-intro">
                  Review the proposed direction and edit anything that&apos;s off. The research and
                  outline will be built to serve this angle and stay out of the excluded topics.
                </p>
                <label className="pf-queue-field-label" htmlFor="brief-angle-primary">
                  Primary angle
                </label>
                <p className="pf-queue-field-hint">
                  The single main angle this article should take.
                </p>
                <textarea
                  id="brief-angle-primary"
                  value={anglePrimary}
                  onChange={(e) => setAnglePrimary(e.target.value)}
                  placeholder="e.g., How post-frame builders select laminated columns for vertical load, durability, and code compliance"
                  rows={3}
                  className="pf-queue-textarea"
                />
                <label className="pf-queue-field-label">Supporting points</label>
                <p className="pf-queue-field-hint">
                  The on-angle points the outline should cover. Add, edit, or remove.
                </p>
                <EditableStringList
                  items={angleSupporting}
                  onChange={setAngleSupporting}
                  placeholder="A supporting point that stays on the primary angle"
                  addLabel="+ Add supporting point"
                />
                <label className="pf-queue-field-label">Out of scope</label>
                <p className="pf-queue-field-hint">
                  Adjacent or easily-confused topics this article should NOT cover.
                </p>
                <EditableStringList
                  items={angleOutOfScope}
                  onChange={setAngleOutOfScope}
                  placeholder="e.g., Beam span / horizontal load tables"
                  addLabel="+ Add out-of-scope topic"
                />
                <div className="pf-modal-actions">
                  <button
                    type="button"
                    onClick={() => setBriefStep('setup')}
                    className="pf-btn pf-btn-secondary"
                    disabled={creatingBrief[showBriefModal]}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCreateResearchBrief(showBriefModal)}
                    disabled={creatingBrief[showBriefModal]}
                    className="pf-btn pf-btn-primary"
                  >
                    {creatingBrief[showBriefModal] ? 'Building…' : 'Approve & build workspace'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showAddModal && (
        <AddArticleModal
          formData={addFormData}
          setFormData={setAddFormData}
          profiles={profiles}
          onSave={handleSubmitAdd}
          onCancel={handleCancelAdd}
        />
      )}
    </div>
  );
}

function AddArticleModal({ formData, setFormData, profiles, onSave, onCancel }) {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'priority' || name === 'profile_id'
        ? (value ? parseInt(value) : null)
        : value
    }));
  };

  return (
    <div className="pf-modal-backdrop" role="presentation">
      <div
        className="pf-modal-panel pf-modal-panel--queue-form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-queue-modal-title"
      >
        <div className="pf-modal-head">
          <h3 id="add-queue-modal-title">Add draft candidate</h3>
          <button type="button" className="pf-modal-close" aria-label="Close" onClick={onCancel}>
            ×
          </button>
        </div>
        <DocumentFormatSelect
          id="add-queue-document-format"
          value={formData.document_format}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, document_format: e.target.value }))
          }
        />
        <div className="form-group">
          <label htmlFor="add-queue-profile">Topic profile (optional)</label>
          <select
            id="add-queue-profile"
            name="profile_id"
            value={formData.profile_id ?? ''}
            onChange={handleInputChange}
            aria-describedby="add-queue-profile-hint"
          >
            <option value="">One-off — no saved topic profile</option>
            {profiles.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </select>
          <small id="add-queue-profile-hint">
            Leave as one-off for ad hoc research requests (like focused research). You can tie a brief
            to a saved profile later when you publish or organize campaigns.
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="add-queue-title">Title *</label>
          <input
            id="add-queue-title"
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
            placeholder="Article title"
          />
        </div>

        <div className="form-group">
          <label htmlFor="add-queue-keyword">Target keyword</label>
          <input
            id="add-queue-keyword"
            type="text"
            name="target_keyword"
            value={formData.target_keyword}
            onChange={handleInputChange}
            placeholder="Primary keyword for this article"
          />
        </div>

        <div className="form-group">
          <label htmlFor="add-queue-priority">Priority (1–10)</label>
          <input
            id="add-queue-priority"
            type="number"
            name="priority"
            value={formData.priority}
            onChange={handleInputChange}
            min="1"
            max="10"
          />
          <small>Higher priority items are surfaced first</small>
        </div>

        <div className="form-group">
          <label htmlFor="add-queue-justification">Justification / summary</label>
          <textarea
            id="add-queue-justification"
            name="justification"
            value={formData.justification}
            onChange={handleInputChange}
            rows="4"
            placeholder="Why this article is valuable, what gap it fills, why it will rank..."
          />
          <small>This context helps build your research brief and guide your writing</small>
        </div>

        <div className="pf-modal-actions">
          <button type="button" onClick={onCancel} className="pf-btn pf-btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={onSave} className="pf-btn pf-btn-primary">
            Add to queue
          </button>
        </div>
      </div>
    </div>
  );
}

function EditQueueItemModal({ item, formData, setFormData, onSave, onCancel }) {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'priority'
        ? (value ? parseInt(value) : null)
        : value
    }));
  };

  return (
    <div className="pf-modal-backdrop" role="presentation">
      <div
        className="pf-modal-panel pf-modal-panel--queue-form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-queue-modal-title"
      >
        <div className="pf-modal-head">
          <h3 id="edit-queue-modal-title">Edit queue item</h3>
          <button type="button" className="pf-modal-close" aria-label="Close" onClick={onCancel}>
            ×
          </button>
        </div>
        <DocumentFormatSelect
          id="edit-queue-document-format"
          value={formData.document_format}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, document_format: e.target.value }))
          }
        />
        <div className="form-group">
          <label htmlFor="edit-queue-title">Title *</label>
          <input
            id="edit-queue-title"
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="edit-queue-keyword">Target keyword</label>
          <input
            id="edit-queue-keyword"
            type="text"
            name="target_keyword"
            value={formData.target_keyword}
            onChange={handleInputChange}
            placeholder="Primary keyword for this article"
          />
        </div>

        <div className="form-group">
          <label htmlFor="edit-queue-priority">Priority (1–10)</label>
          <input
            id="edit-queue-priority"
            type="number"
            name="priority"
            value={formData.priority}
            onChange={handleInputChange}
            min="1"
            max="10"
          />
          <small>Higher priority items are surfaced first</small>
        </div>

        <div className="form-group">
          <label htmlFor="edit-queue-justification">Justification / summary</label>
          <textarea
            id="edit-queue-justification"
            name="justification"
            value={formData.justification}
            onChange={handleInputChange}
            rows="4"
            placeholder="Why this article is valuable, what gap it fills, why it will rank..."
          />
        </div>

        <div className="pf-modal-actions">
          <button type="button" onClick={onCancel} className="pf-btn pf-btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={onSave} className="pf-btn pf-btn-primary">
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default ContentQueue;
